import { Context, Data, Effect, FileSystem, Layer, Path } from 'effect';

const ConfigTypeId = 'okrapdf/cli/ServeRuntimeConfig/TypeId' as const;
const FilesTypeId = 'okrapdf/cli/ServeJsonFiles/TypeId' as const;

export class ServeIoError extends Data.TaggedError('ServeIoError')<{
  readonly message: string;
  readonly method: string;
  readonly path?: string;
  readonly cause?: unknown;
}> {}

export interface ServeRuntimeSnapshot {
  readonly [ConfigTypeId]: typeof ConfigTypeId;
  readonly env: Readonly<Record<string, string | undefined>>;
  readonly bundleDir: string;
  readonly dataDir?: string;
  readonly builtin?: boolean;
  readonly publicDir?: string;
  readonly npmPackageVersion: string;
}

export interface ServeRuntimeConfigOptions {
  readonly env: Record<string, string | undefined>;
  readonly bundleDir: string;
  readonly dataDir?: string;
  readonly builtin?: boolean;
  readonly publicDir?: string;
}

/**
 * Read process.env exactly once at a serve/self-host composition root.
 * Callers that inject `env` or a deterministic `dataDir` stay isolated from
 * ambient DATABASE_URL / API key values.
 */
export function resolveServeRuntimeEnv(options: {
  readonly env?: Record<string, string | undefined>;
  readonly dataDir?: string;
}): Record<string, string | undefined> {
  if (options.env) return { ...options.env };
  if (options.dataDir !== undefined) return {};
  return { ...process.env };
}

export const ServeRuntimeConfig: Context.Service<ServeRuntimeSnapshot, ServeRuntimeSnapshot> =
  Context.Service('okrapdf/cli/ServeRuntimeConfig');

export function makeServeRuntimeConfig(options: ServeRuntimeConfigOptions): ServeRuntimeSnapshot {
  return ServeRuntimeConfig.of({
    [ConfigTypeId]: ConfigTypeId,
    env: options.env,
    bundleDir: options.bundleDir,
    ...(options.dataDir === undefined ? {} : { dataDir: options.dataDir }),
    ...(options.builtin === undefined ? {} : { builtin: options.builtin }),
    ...(options.publicDir === undefined ? {} : { publicDir: options.publicDir }),
    npmPackageVersion: options.env.npm_package_version ?? 'dev',
  });
}

export function layerFromOptions(
  options: ServeRuntimeConfigOptions,
): Layer.Layer<ServeRuntimeSnapshot> {
  return Layer.succeed(ServeRuntimeConfig, makeServeRuntimeConfig(options));
}

export const layerMemory: Layer.Layer<ServeRuntimeSnapshot> = layerFromOptions({
  env: {},
  bundleDir: '/tmp/okra-serve-runtime-memory',
});

export interface ServeJsonFiles {
  readonly [FilesTypeId]: typeof FilesTypeId;
  readonly exists: (path: string) => Effect.Effect<boolean, ServeIoError>;
  readonly readJson: (path: string) => Effect.Effect<unknown, ServeIoError>;
  readonly writeJson: (path: string, value: unknown) => Effect.Effect<void, ServeIoError>;
}

export const ServeJsonFiles: Context.Service<ServeJsonFiles, ServeJsonFiles> = Context.Service(
  'okrapdf/cli/ServeJsonFiles',
);

export interface ServeJsonFilesMakeOptions {
  readonly exists: (path: string) => Effect.Effect<boolean, ServeIoError>;
  readonly readJson: (path: string) => Effect.Effect<unknown, ServeIoError>;
  readonly writeJson: (path: string, value: unknown) => Effect.Effect<void, ServeIoError>;
}

export function makeServeJsonFiles(options: ServeJsonFilesMakeOptions): ServeJsonFiles {
  return ServeJsonFiles.of({
    [FilesTypeId]: FilesTypeId,
    exists: options.exists,
    readJson: options.readJson,
    writeJson: options.writeJson,
  });
}

function mapServeIo<A, E, R>(
  method: string,
  path: string,
  effect: Effect.Effect<A, E, R>,
): Effect.Effect<A, ServeIoError, R> {
  return Effect.mapError(
    effect,
    (cause) =>
      new ServeIoError({
        method,
        path,
        message: `ServeJsonFiles.${method} failed`,
        cause,
      }),
  );
}

/** Portable JSON-file layer. The Node composition root supplies FileSystem + Path. */
export function layerJsonFiles(): Layer.Layer<
  ServeJsonFiles,
  never,
  FileSystem.FileSystem | Path.Path
> {
  return Layer.effect(
    ServeJsonFiles,
    Effect.gen(function* () {
      const fileSystem = yield* FileSystem.FileSystem;
      const paths = yield* Path.Path;
      return makeServeJsonFiles({
        exists: (path) => mapServeIo('exists', path, fileSystem.exists(path)),
        readJson: (path) =>
          mapServeIo(
            'readJson',
            path,
            Effect.gen(function* () {
              const content = yield* fileSystem.readFileString(path);
              return yield* Effect.try(() => JSON.parse(content) as unknown);
            }),
          ),
        writeJson: (path, value) =>
          mapServeIo(
            'writeJson',
            path,
            Effect.gen(function* () {
              const content = yield* Effect.try(() => `${JSON.stringify(value, null, 2)}\n`);
              yield* fileSystem.makeDirectory(paths.dirname(path), { recursive: true });
              yield* fileSystem.writeFileString(path, content);
            }),
          ),
      });
    }),
  );
}

/** Process-local JSON files for tests. */
export const layerJsonFilesMemory: Layer.Layer<ServeJsonFiles> = Layer.sync(ServeJsonFiles, () => {
  const files = new Map<string, string>();
  return makeServeJsonFiles({
    exists: (path) => Effect.sync(() => files.has(path)),
    readJson: (path) =>
      Effect.try({
        try: () => {
          const raw = files.get(path);
          if (raw === undefined) throw new Error(`missing ${path}`);
          return JSON.parse(raw) as unknown;
        },
        catch: (cause) =>
          new ServeIoError({
            method: 'readJson',
            path,
            message: `ServeJsonFiles.readJson failed`,
            cause,
          }),
      }),
    writeJson: (path, value) =>
      Effect.sync(() => {
        files.set(path, `${JSON.stringify(value, null, 2)}\n`);
      }),
  });
});
